/**
 * Mechanisms + TrustBlock — the substance of the home page.
 *
 * WHAT THIS REPLACES. The home page used to answer "what does it do?" with a
 * 15-tile marketplace grid and a 30-logo marquee. Both are breadth claims, and
 * breadth is not what the research says a law firm buys on: the barriers they
 * report are data security, ethics, privilege and trust in results, and the
 * accelerator they name above cost is integration. A tile that says
 * "Integrates with any system" answers none of that. A sentence that says
 * exactly HOW it integrates — and admits the second lane — answers it.
 *
 * So each block states a MECHANISM, and every number in one comes from the
 * constant the product actually enforces. There are no superlatives here on
 * purpose: 39% of legal professionals name "trust in results" as a barrier,
 * and an unverifiable claim spends the trust it is trying to build.
 *
 * THE TWO LANES, deliberately honest. The old logo wall showed Clio, Clio
 * Manage, LawPay, PracticePanther and MyCase — none of which has a direct
 * connector. That is defensible only if the page says so, because the browser
 * lane genuinely covers them. It says so.
 *
 * NO APP COUNT. "~1,209 toolkits" exists only as a hand-maintained comment in
 * ComposioClient.ts. A number on a marketing page with nothing checking it is
 * the exact drift class this work has been correcting, so the copy states the
 * two lanes and leaves the count out until it has a constant and a gate.
 *
 * Every claim in TrustBlock is code-backed — see the per-line notes. Anything
 * inherited from Google Cloud is attributed to Google Cloud; the phrase
 * blocklist in scripts/check-security-claims.mjs is the backstop.
 */

import React from 'react';
import styled from 'styled-components';
import KeepCase from '../common/KeepCase';
import { MAX_FILES_PER_REQUEST_UI } from '../../config/composerLimits';

const Section = styled.section`
  padding: 4.5rem 1.5rem;
  background: var(--brand-canvas);
`;

const Inner = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const Head = styled.h2`
  font-family: 'Satoshi', 'Inter', sans-serif;
  font-size: clamp(1.5rem, 3.6vw, 2.1rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--brand-text);
  margin: 0 0 0.6rem;
`;

const Lead = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: var(--brand-text-secondary);
  max-width: 62ch;
  margin: 0 0 2.25rem;
`;

const List = styled.div`
  display: grid;
  gap: 1rem;
`;

const Block = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 15rem) minmax(0, 1fr);
  gap: 1.25rem;
  padding: 1.35rem 0 0;
  border-top: 1px solid var(--brand-line);

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    gap: 0.45rem;
  }
`;

const BlockTitle = styled.h3`
  font-family: 'Satoshi', 'Inter', sans-serif;
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--brand-text);
  margin: 0;
`;

const BlockBody = styled.div`
  font-size: 0.95rem;
  line-height: 1.65;
  color: var(--brand-text-secondary);

  strong {
    color: var(--brand-text);
    font-weight: 600;
  }

  p { margin: 0 0 0.55rem; }
  p:last-child { margin: 0; }
`;

const Jump = styled.a`
  color: var(--brand-gold-text);
  text-decoration: none;
  border-bottom: 1px solid var(--brand-line-strong);

  &:hover { border-bottom-color: var(--brand-gold); }
  &:focus-visible {
    outline: 2px solid var(--brand-gold);
    outline-offset: 2px;
  }
`;

export const Mechanisms: React.FC = () => (
  <Section id="what-it-does">
    <Inner>
      <Head>what it actually does</Head>
      <Lead>
        five things, each one a mechanism rather than a promise. every number here is the
        limit the product enforces, not a round figure.
      </Lead>

      <List>
        <Block>
          <BlockTitle>the digital work, done</BlockTitle>
          <BlockBody>
            {/* Routines: platform-guide language, "not a cron job". */}
            <p>
              you set up recurring work by asking for it — <em>whenever a new email from the
              county arrives, file it</em> — and it runs without you.
            </p>
            {/* The held-send contract: reversibility governs autonomy. */}
            <p>
              anything that <strong>leaves</strong> your firm waits for the person accountable
              for it. reading and preparing are free; sending is yours.
            </p>
            {/* run_computer_task, Tier 19 — and the password wall is real. */}
            <p>
              for a system with no way in — a court portal, a vendor site, a web form — it works
              in a real browser on your workspace computer, and you can watch it happen.
            </p>
          </BlockBody>
        </Block>

        <Block>
          <BlockTitle>reads everything you have</BlockTitle>
          <BlockBody>
            <p>
              up to <strong>{MAX_FILES_PER_REQUEST_UI} files in one request</strong> — scanned
              or native — read, indexed, and returned as <strong>one deliverable</strong> with the
              obligations, dates and risks pulled out.
            </p>
            <p>not a sample of them. all of them.</p>
          </BlockBody>
        </Block>

        <Block>
          <BlockTitle>learns how your firm works</BlockTitle>
          <BlockBody>
            <p>
              correct it once and it holds. every night it consolidates what the day taught it,
              so accuracy grows with use instead of resetting.
            </p>
            <p>
              that is the part training budgets never cover — the tool learning your way of
              doing things, rather than your people learning the tool's.
            </p>
          </BlockBody>
        </Block>

        <Block>
          <BlockTitle>connects to what you already use</BlockTitle>
          <BlockBody>
            {/* Two lanes, named. This is what makes the practice-management
                logos honest: they have no direct connector, and the browser
                lane is how they actually work. */}
            <p>
              <strong>two ways in.</strong> the apps with a real connection — mail, calendar,
              drive, documents, e-signature, accounting — connect directly.
            </p>
            <p>
              everything else, <KeepCase>Prest0n</KeepCase> signs in to{' '}
              <strong>the way a person would</strong>: in a real browser on your workspace
              computer, with you typing the password and the one-time code. that covers the
              practice-management and court systems that have no way in at all.
            </p>
          </BlockBody>
        </Block>

        <Block>
          <BlockTitle>only ever does what the role allows</BlockTitle>
          <BlockBody>
            <p>
              permissions are resolved for the person asking, on <strong>every single
              message</strong>, from your live team record. a role that does not include
              something is refused — every time, not filtered afterwards.
            </p>
            <p>
              <Jump href="#the-room">try it below →</Jump>
            </p>
          </BlockBody>
        </Block>
      </List>
    </Inner>
  </Section>
);

const TrustSection = styled(Section)`
  background: var(--brand-surface);
  border-top: 1px solid var(--brand-line);
`;

const Facts = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 0.85rem;
  max-width: 72ch;
`;

const Fact = styled.li`
  font-size: 0.95rem;
  line-height: 1.65;
  color: var(--brand-text-secondary);
  padding-left: 1.15rem;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.62em;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--brand-gold);
  }

  strong {
    color: var(--brand-text);
    font-weight: 600;
  }
`;

const More = styled.p`
  margin: 1.75rem 0 0;
  font-size: 0.95rem;
`;

/**
 * Only code-backed claims. Each line's basis, in order:
 *  1. provider-contract commitment, stated as one.
 *  2. per-org isolated VM (W-R2: every workspace gets its own).
 *  3. the 2026-08-12 actor-only directive — resolution is per actor, per message.
 *  4. append-only audit feed, server-only writes, members may read.
 *  5. held outward sends; the authorize click is a human's.
 *  6. the co-drive wall at passwords and 2FA (D19) — machinery, not a promise.
 *  7. verified: no GA/GTM/Segment/PostHog/Intercom/Hotjar on the public site.
 *     Stronger than a no-op since 2026-09-11: the whole consent/tracking
 *     subtree was DELETED rather than disabled, so there is no dormant
 *     analytics path to be switched back on and no consent record to
 *     fabricate. Rare, checkable, and exactly what a privilege-conscious
 *     buyer notices.
 *  8. inherited infrastructure, attributed to Google Cloud rather than claimed.
 */
export const TrustBlock: React.FC = () => (
  <TrustSection id="how-data-is-handled">
    <Inner>
      <Head>how your data is handled</Head>
      <Lead>
        written for whoever has to sign off on this. every line below is something the code
        does — not a policy we intend to adopt.
      </Lead>

      <Facts>
        <Fact>your work is <strong>never used to train a model</strong>.</Fact>
        <Fact>
          every organization gets its <strong>own private workspace computer</strong> — not a
          shared tenant.
        </Fact>
        <Fact>
          permissions are resolved <strong>for the person asking, on every single message</strong>,
          from your live team record. a role that does not include something is refused, every
          time — never filtered afterwards.
        </Fact>
        <Fact>governed actions land in an <strong>append-only audit feed</strong> your team can read.</Fact>
        <Fact>
          anything that leaves your firm — an email, a message to a client —{' '}
          <strong>waits for the person accountable for it</strong> to authorize it.
        </Fact>
        <Fact>
          <strong>passwords and one-time codes are always typed by you.</strong>{' '}
          <KeepCase>Prest0n</KeepCase> stops at those walls by design.
        </Fact>
        <Fact>
          this website runs <strong>no analytics and no third-party trackers</strong>. nothing
          about your visit is sold or shared.
        </Fact>
        <Fact>
          we run on Google Cloud — encrypted at rest, TLS 1.3 in transit, hosted in the united
          states.
        </Fact>
      </Facts>

      <More>
        <Jump href="/security">read the full security posture →</Jump>
      </More>
    </Inner>
  </TrustSection>
);

export default Mechanisms;
